// Deterministic sigil generator — ported verbatim from the original index.html.
// Same FNV-1a hash, same mulberry-ish PRNG, same SVG strings → identical glyphs.
//
// Output is an SVG *markup string* (the original returned a string and was
// injected via innerHTML; we keep that for fidelity and perf — building these
// glyphs as Svelte template nodes would be far slower for ~70 cards).

import { CUR, LAYER, type Entry, type RegId } from './data/grimoire';

const SIG_ACCENT: Record<RegId, string> = {
  'reg-1': '#9098a2',
  'reg-2': '#cfd8de',
  'reg-3': '#e0a93a',
  'reg-4': '#c8443b',
};

export function _hsh(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// mulberry32-style PRNG; arrow returns next float in [0,1).
export function _rng(a: number): () => number {
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function _centerGlyph(cur: string | null, col: string): string {
  switch (cur) {
    case 'perception':
      return `<path d="M44.5,45.5 a5.5,5.5 0 0,1 11,0" fill="none"/><circle cx="50" cy="50.5" r="3.2" fill="none"/><path d="M50,53.7 v5.5 M46.6,56 h6.8"/>`;
    case 'body':
      return `<path d="M50,44 L55.5,53.5 H44.5 Z" fill="none"/><path d="M50,53.5 v5.5 M46.8,56 h6.4"/>`;
    case 'self':
      return `<path d="M53.5,43.8 a7.2,7.2 0 1,0 0,12.4 a5.4,5.4 0 1,1 0,-12.4" fill="none"/>`;
    case 'word':
      return `<circle cx="50" cy="50" r="5.4" fill="none"/><path d="M44.4,50 h11.2"/>`;
    case 'void':
      return `<circle cx="50" cy="50" r="5.6" fill="none"/><circle cx="50" cy="50" r="1.2" fill="${col}" stroke="none"/>`;
    case 'field':
      return `<path d="M44.6,46 H55.4 L50,56.4 Z" fill="none"/>`;
    case 'dream':
      return `<path d="M50,44.4 L55.8,55 H44.2 Z" fill="none"/><path d="M46.4,51 h7.2"/>`;
    case 'depth':
      return `<path d="M50,43.6 L55.6,53.2 H44.4 Z" fill="none"/><path d="M50,56.4 L44.4,46.8 H55.6 Z" fill="none"/>`;
    default:
      return `<circle cx="50" cy="50" r="5.4" fill="none"/><path d="M45.8,54.2 L54.2,45.8"/>`;
  }
}

function _rune(t: number, x: number, y: number, ang: number, col: string): string {
  let p: string;
  switch (t) {
    case 0:
      p = '<path d="M0,-4.2 V4.2"/>';
      break;
    case 1:
      p = '<path d="M0,4 V-0.6 M0,-0.6 L-2.6,-4 M0,-0.6 L2.6,-4"/>';
      break;
    case 2:
      p = '<path d="M-3,-3 L3,3 M-3,3 L3,-3"/>';
      break;
    case 3:
      p = '<path d="M-3,-4 L0,0.4 L3,-4"/>';
      break;
    case 4:
      p = '<circle cx="0" cy="0" r="2.1" fill="none"/>';
      break;
    case 5:
      p = '<path d="M-3,-3 H3 M0,-3 V4"/>';
      break;
    default:
      p = `<circle cx="0" cy="-3" r=".85" fill="${col}"/><circle cx="-2.2" cy="1.2" r=".85" fill="${col}"/><circle cx="2.2" cy="1.2" r=".85" fill="${col}"/>`;
  }
  return `<g transform="translate(${x.toFixed(1)},${y.toFixed(1)}) rotate(${ang.toFixed(0)})">${p}</g>`;
}

export function buildSigil(e: Entry): string {
  const col = e.cur ? CUR[e.cur].cc : SIG_ACCENT[e._reg as RegId];
  const seed = _hsh(
    e.id +
      '|' +
      e.nm +
      '|' +
      (e.cur || '-') +
      '|' +
      (e._reg || '-') +
      '|' +
      (e.conf || e.tier || '') +
      '|' +
      (e.levels || []).length +
      '|' +
      (LAYER[e.id] || ''),
  );
  const rnd = _rng(seed);
  const TAU = 6.28319;
  const tenuous = e.tier === 'C' || e.conf === 'low';

  let scaff = `<circle cx="50" cy="50" r="46" fill="none"${tenuous ? ' stroke-dasharray="3 3.5"' : ''}/>`;
  if (e._reg === 'reg-2') scaff += '<circle cx="50" cy="50" r="42.5" fill="none" opacity=".5"/>';
  if (e._reg === 'reg-3') {
    const dn = 30;
    for (let i = 0; i < dn; i++) {
      const a = (i / dn) * TAU;
      scaff += `<circle cx="${(50 + 48.5 * Math.cos(a)).toFixed(1)}" cy="${(50 + 48.5 * Math.sin(a)).toFixed(1)}" r=".7" fill="${col}" stroke="none"/>`;
    }
  }
  if (e._reg === 'reg-4') {
    const sn = 18;
    for (let i = 0; i < sn; i++) {
      const a = (i / sn) * TAU;
      scaff += `<path d="M${(50 + 46 * Math.cos(a)).toFixed(1)},${(50 + 46 * Math.sin(a)).toFixed(1)} L${(50 + 50 * Math.cos(a)).toFixed(1)},${(50 + 50 * Math.sin(a)).toFixed(1)}"/>`;
    }
  }

  const tn = 8 + ((e.levels || []).length ? 4 : 0) + (seed % 5);
  let ticks = '';
  for (let i = 0; i < tn; i++) {
    const a = (i / tn) * TAU;
    const r2 = rnd() > 0.72 ? 40 : 42.5;
    ticks += `<path d="M${(50 + r2 * Math.cos(a)).toFixed(1)},${(50 + r2 * Math.sin(a)).toFixed(1)} L${(50 + 45 * Math.cos(a)).toFixed(1)},${(50 + 45 * Math.sin(a)).toFixed(1)}"/>`;
  }

  const K = 3 + Math.floor(rnd() * 4);
  const base = rnd() * TAU;
  let runes = '';
  for (let i = 0; i < K; i++) {
    const a = base + (i / K) * TAU + (rnd() - 0.5) * 0.3;
    runes += _rune(
      Math.floor(rnd() * 7),
      50 + 35 * Math.cos(a),
      50 + 35 * Math.sin(a),
      (a * 57.3 + 90),
      col,
    );
  }

  const pts = 12,
    R = 24,
    ang = (i: number) => (i / pts) * TAU - 1.5708,
    px = (i: number) => 50 + R * Math.cos(ang(i)),
    py = (i: number) => 50 + R * Math.sin(ang(i));
  const nm = (e.nm || '').toLowerCase().replace(/[^a-zа-яё]/g, '');
  let seq: number[] = [];
  for (const ch of nm) {
    const v = ch.charCodeAt(0) % pts;
    if (seq[seq.length - 1] !== v) seq.push(v);
  }
  if (seq.length < 2)
    seq = [seed % pts, ((seed % pts) + 4 + (seed % 5)) % pts];

  let d = 'M' + px(seq[0]).toFixed(1) + ',' + py(seq[0]).toFixed(1);
  for (let i = 1; i < seq.length; i++)
    d += ' L' + px(seq[i]).toFixed(1) + ',' + py(seq[i]).toFixed(1);

  const sx = px(seq[0]),
    sy = py(seq[0]),
    ex = px(seq[seq.length - 1]),
    ey = py(seq[seq.length - 1]);
  const a2 =
    Math.atan2(ey - py(seq[seq.length - 2]), ex - px(seq[seq.length - 2])) +
    1.5708;
  const bar = `M${(ex + 3 * Math.cos(a2)).toFixed(1)},${(ey + 3 * Math.sin(a2)).toFixed(1)} L${(ex - 3 * Math.cos(a2)).toFixed(1)},${(ey - 3 * Math.sin(a2)).toFixed(1)}`;
  const cd = tenuous ? ' stroke-dasharray="4 3"' : '';

  return (
    `<svg class="sigil" viewBox="0 0 100 100" aria-hidden="true">` +
    `<g class="sig-rot" stroke="${col}" fill="none" stroke-width="1.6" stroke-linecap="round">${scaff}${ticks}<g fill="${col}">${runes}</g></g>` +
    `<g class="sig-core" stroke="${col}" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"${cd}><path d="${d}"/><circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="2.1"/><path d="${bar}"/></g>` +
    `<g class="sig-center" stroke="${col}" fill="none" stroke-width="1.5" stroke-linecap="round">${_centerGlyph(e.cur, col)}</g></svg>`
  );
}
